import './color-picker.css';

import { cn, type createStyleType, Input, inputVariants } from '@ctct/summit-core';
import { useSummitI18n } from '@ctct/summit-core';
import { type ChangeEvent, type ComponentPropsWithoutRef } from 'react';
import { HexAlphaColorPicker, HexColorInput, HexColorPicker } from 'react-colorful';

const themeableVars = ['r:[--text:var(--sds-input-text-default)]'] as const;
type ColorPickerStyle = ReturnType<typeof createStyleType<typeof themeableVars>>;

interface ColorPickerProps extends ComponentPropsWithoutRef<'div'> {
  /** Selected color, expressed in six-digit hex (`#rrggbb`). */
  color: string;
  /** The transparency of the selected color (0-100). */
  opacity?: number;
  /** Callback function that is called when a color is selected. */
  onColorSelect: (selectedColor: string) => void;
  /** Set inline styles, including the component's built-in CSS variables. */
  style?: ColorPickerStyle;
  /** If true, the opacity slider and input will be hidden. Defaults to false. */
  hideOpacity?: boolean;
}

function hexToPercent(hex: string) {
  const decimal = parseInt(hex, 16);
  return Math.round((decimal / 255) * 100);
}

function percentToHex(percent: number) {
  const decimal = Math.round((percent / 100) * 255);
  const hex = decimal.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

function extractColor(color: string) {
  const normalizedColor = color.startsWith('#') ? color : `#${color}`;
  if (normalizedColor.length <= 7) return { hex: normalizedColor, opacity: 100 };

  const hex = normalizedColor.slice(0, 7);
  const alphaHex = normalizedColor.slice(7, 9);
  const alphaPercent = hexToPercent(alphaHex);
  const opacity = Number.isNaN(alphaPercent) ? 100 : alphaPercent;
  return { hex, opacity };
}

/**
 * A color picker component that allows users to select a color and set its transparency.
 */
const ColorPicker = ({
  color,
  opacity: opacityProp,
  onColorSelect: handleColorSelect,
  className,
  hideOpacity = false,
  ...props
}: ColorPickerProps) => {
  const { tExtended } = useSummitI18n();
  const { hex, opacity: extractedOpacity } = extractColor(color);
  const isOpacityControlled = opacityProp !== undefined;
  const currentOpacity = isOpacityControlled ? opacityProp : extractedOpacity;

  const inputStyles = 'r:text-(--text) r:text-base';
  const PickerComponent = !hideOpacity ? HexAlphaColorPicker : HexColorPicker;
  const hexOpacity = currentOpacity === 100 ? '' : percentToHex(currentOpacity);
  const colorForPicker = hideOpacity ? hex : `${hex}${hexOpacity}`;

  const handleHexChange = (newHex: string) => {
    let newColor = newHex;
    if (!hideOpacity) {
      const newHexOpacity = currentOpacity === 100 ? '' : percentToHex(currentOpacity);
      newColor = `${newHex}${newHexOpacity}`;
    }
    handleColorSelect(newColor);
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (hideOpacity) return;
    const rawValue = Number(e.target.value);
    const value = Math.max(0, Math.min(100, rawValue));
    const newOpacityHex = value === 100 ? '' : percentToHex(value);
    const newColor = value === 100 ? hex : `${hex}${newOpacityHex}`;
    handleColorSelect(newColor);
  };

  return (
    <div className={cn('rds-color-picker', className)} data-sds-id="color-picker" {...props}>
      <PickerComponent color={colorForPicker} onChange={handleColorSelect} />
      <div className="r:relative r:flex r:mt-5">
        <HexColorInput
          color={hex}
          prefixed
          onChange={handleHexChange}
          aria-label={tExtended('colorPicker.hexValueAriaLabel', { hex })}
          aria-live="polite"
          className={cn(
            'rds-colorpicker-hex',
            inputVariants({ variant: 'md' }),
            themeableVars.join(' '),
            inputStyles,
            !hideOpacity
              ? 'r:rounded-r-none r:focus:z-raised r:focus-visible:ring-0'
              : 'r:focus-visible:ring-0'
          )}
          data-testid="color-picker-hex"
        />
        {!hideOpacity && (
          <Input
            type="number"
            value={currentOpacity}
            max={100}
            min={0}
            maxLength={3}
            aria-label={tExtended('colorPicker.opacityAriaLabel', { opacity: currentOpacity })}
            onChange={handleTextChange}
            className={cn('rds-colorpicker-opacity', 'r:border-l-0 r:rounded-l-none r:pr-8')}
            data-testid="color-picker-opacity"
            endAdornment={<span className="r:absolute r:left-auto r:right-2">%</span>}
          />
        )}
      </div>
    </div>
  );
};

export { ColorPicker, type ColorPickerProps };
